<script lang="ts">
import { type Component, computed, onBeforeUnmount, onMounted, type Ref, ref, useAttrs, useId } from 'vue';

import {
  useVideoOptions,
  type VideoCaption,
  type VideoSource,
  type VideoTrackingOptions,
} from '@sg-core/src/View/Components/Molecules/VideoPlayer';

import { BackgroundVideo, type BackgroundVideoOptions } from './background-video';

import type Plyr from 'plyr';

export type { VideoSource, VideoCaption };

export interface PhxsgBackgroundVideoProps {
  id?: string | null;
  rootClass?: string | null;
  initialize?: boolean;
  embed?: string | null;
  prefetch?: boolean;
  videoTracking?: VideoTrackingOptions;
  plyr?: boolean;
  plyrOptions?: Plyr.Options;
  sources?: VideoSource[];
  captions?: VideoCaption[];
  poster?: string | null;
  lazy?: boolean;
  autoplay?: boolean;
  loop?: boolean;

  /**
   * The VideoPlayer component to use.
   * Maisons should pass their own VideoPlayer component to get proper styling.
   */
  videoPlayerComponent: Component;
}

interface BackgroundVideoMountPayload {
  player: Ref<BackgroundVideo | null>;
  playerElement: Ref<HTMLElement | null>;
  BackgroundVideo: typeof BackgroundVideo;
}
</script>

<script setup lang="ts">
defineOptions({ inheritAttrs: false });

const {
  id = null,
  rootClass = null,
  initialize = true,
  embed = null,
  prefetch = false,
  videoTracking = {},
  plyr = false,
  plyrOptions = {},
  sources = [],
  captions = [],
  poster = null,
  lazy = true,
  autoplay = true,
  loop = true,
  videoPlayerComponent,
} = defineProps<PhxsgBackgroundVideoProps>();

const emit = defineEmits<{
  'background-video:mounted': [BackgroundVideoMountPayload];
  play: [Event];
  pause: [Event];
}>();

// Fallthrough attrs are forwarded to the underlying <video> element (see phxsg-video-player-video.vue).
const attrs = useAttrs();
const isDecorative = computed(() => !('aria-label' in attrs) && !('aria-labelledby' in attrs) && !('title' in attrs));

// Composables
const videoOptions = useVideoOptions(() => ({
  plyr,
  plyrOptions,
  prefetch,
  videoTracking,
}));

// Refs
const player: Ref<BackgroundVideo | null> = ref(null);
const videoPlayerRef = ref<{ playerElement?: HTMLElement | null } | null>(null);

// Computed ref to underlying DOM element
const playerElement = computed(() => videoPlayerRef.value?.playerElement ?? null);

// ID generation
const generatedId = useId();
const computedId = computed(() => id ?? `background-video-${generatedId}`);

// Lifecycle
onMounted(() => {
  if (!initialize) {
    emit('background-video:mounted', {
      player,
      playerElement: playerElement as unknown as Ref<HTMLElement | null>,
      BackgroundVideo,
    });
    return;
  }

  const element = playerElement.value;
  if (element) {
    player.value = new BackgroundVideo(element, {
      ...videoOptions.value,
      autoplay,
      muted: true,
      embed: embed ?? undefined,
    } as BackgroundVideoOptions);
  }

  emit('background-video:mounted', {
    player,
    playerElement: playerElement as unknown as Ref<HTMLElement | null>,
    BackgroundVideo,
  });
});

onBeforeUnmount(() => {
  player.value?.destroy?.();
});

defineExpose({ playerElement, player });
</script>

<template>
  <component
    :is="videoPlayerComponent"
    :id="computedId"
    ref="videoPlayerRef"
    :root-class="['phxsg-background-video', rootClass]"
    :embed="embed"
    :prefetch="prefetch"
    :video-tracking="videoTracking"
    :plyr="plyr"
    :plyr-options="plyrOptions"
    :sources="sources"
    :captions="captions"
    :poster="poster"
    :lazy="lazy"
    :autoplay="autoplay"
    :loop="loop"
    :aria-hidden="isDecorative ? 'true' : null"
    :initialize="false"
    :controls="false"
    :muted="true"
    :playsinline="true"
    :play-button-overlay="false"
    fit="cover"
    class="phxsg-background-video__video"
    v-bind="$attrs"
    @play="emit('play', $event)"
    @pause="emit('pause', $event)"
  >
    <slot />
  </component>
</template>

<style src="./background-video.css" />
